import { Box, Container, Typography, Grid, Card, useTheme, Button } from '@mui/material';
import { motion } from 'framer-motion';
import VerifiedIcon from '@mui/icons-material/Verified';
import HandshakeIcon from '@mui/icons-material/Handshake';
import GroupsIcon from '@mui/icons-material/Groups';
import { Link as RouterLink } from 'react-router-dom';

const About = () => {
  const theme = useTheme();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.8)), url('/images/IMG_9728.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          pt: { xs: '100px', sm: '120px', md: '140px' },
          pb: { xs: '60px', sm: '80px', md: '100px' },
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h1"
              align="center"
              sx={{
                fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                fontWeight: 800,
                mb: 3,
                textShadow: '2px 4px 8px rgba(0,0,0,0.2)',
              }}
            >
              About Us
            </Typography>
            <Typography
              variant="h5"
              align="center"
              sx={{
                maxWidth: '800px',
                mx: 'auto',
                mb: 4,
                lineHeight: 1.8,
                color: 'rgba(255,255,255,0.9)',
              }}
            >
                            Dedicated to serving those who served us, we connect generous vehicle donors with
              Young Veterans, Single Parent Veteran Families, and All Those Who Served in their time
              of need, creating a lasting positive impact benefiting our Veteran Community.

            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Our Story Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    mb: 4,
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Our Story
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    color: 'text.secondary',
                    lineHeight: 1.8,
                    fontSize: '1.1rem',
                  }}
                >
                  Partnered with all types of organizations in the automotive remarketing industry, the team at Vehicles For
                  Young Veterans strive to maximize the proceeds from each and every donated vehicle to
                  better assist our Veteran Community and our generous donors. We understand that post
                  service life can be challenging, and that&#39;s why we&#39;ve dedicated ourselves to making a
                  difference through vehicle donations.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 4,
                    color: 'text.secondary',
                    lineHeight: 1.8,
                    fontSize: '1.1rem',
                  }}
                >
                  Our mission extends beyond just vehicle donations - we&#39;re building a support network
                  that helps veterans rebuild their lives, achieve independence, and create lasting positive
                  change in our community.
                </Typography>
                <Box sx={{ 
                  my: 4, 
                  p: 3, 
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(0,0,0,0.02)',
                  borderRadius: '0 8px 8px 0'
                }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontStyle: 'italic',
                      mb: 2,
                      color: 'text.primary',
                      lineHeight: 1.8
                    }}
                  >
                    &quot;Our debt to the heroic men and valiant women in the service of our country can never
                    be repaid. They have earned our undying gratitude&quot;
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 600
                    }}
                  >
                    Harry S. Truman
                  </Typography>
                </Box>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  color="primary"
                  size="large"
                >
                  Get In Touch
                </Button>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      overflow: 'hidden',
                      borderRadius: 4,
                      position: 'relative',
                    }}
                  >
                    <Box
                      component="img"
                      src="/images/IMG_9725.jpeg"
                      alt="Veterans Support"
                      sx={{
                        width: '100%',
                        height: 400,
                        objectFit: 'cover',
                      }}
                    />
                  </Card>
                </motion.div>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h2"
              align="center"
              sx={{
                mb: 6,
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Why Choose VFYV
            </Typography>
          </motion.div>
          <Grid container spacing={4}>
            {[
              {
                icon: <VerifiedIcon sx={{ fontSize: 40 }} />,
                title: "Licensed & Verified",
                description: "We are a fully licensed and verified organization dedicated to supporting veterans and their families."
              },
              {
                icon: <GroupsIcon sx={{ fontSize: 40 }} />,
                title: "Community Impact",
                description: "Through your generosity and our vehicle donation programs and support services, Young Veterans, Single Parent Veterans, and our Veteran Community directly benefit! Join us and directly impact a Veterans life TODAY!"
              },
              {
                icon: <HandshakeIcon sx={{ fontSize: 40 }} />,
                title: "Direct Support",
                description: "Your donations go directly to supporting veterans through trusted foundations like Purple Heart, Tunnels to Towers, and Bob Woodruff Foundation."
              },
              
            ].map((feature, index) => (
              <Grid item xs={12} md={4} key={index} sx={{ display: 'flex' }}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: 2,
                      background: 'rgba(255,255,255,0.8)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      transition: 'transform 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                        color: 'white',
                        mx: 'auto',
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      align="center"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      align="center"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.8,
                        flex: 1
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Impact Section */}
      <Box 
        sx={{ 
          py: { xs: 8, md: 12 }, 
          backgroundColor: 'background.default',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(rgba(0, 0, 0, 0.03), rgba(0, 0, 0, 0.06))`,
          }
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h2"
              align="center"
              sx={{
                mb: 6,
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Our Impact
            </Typography>
          </motion.div>
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 3,
                }}
              >
                {['/images/IMG_9838.jpeg', '/images/IMG_9837.jpeg', '/images/IMG_9824.jpeg', '/images/4.jpeg'].map((img, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.2 }}
                    viewport={{ once: true }}
                  >
                    <Card
                      elevation={2}
                      sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        height: '100%',
                      }}
                    >
                      <Box
                        component="img"
                        src={img}
                        alt="Veterans Support"
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                        }}
                      />
                    </Card>
                  </motion.div>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    mb: 4,
                    fontWeight: 700,
                  }}
                >
                  Making a Difference
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    color: 'text.secondary',
                    lineHeight: 1.8,
                    fontSize: '1.1rem',
                  }}
                >
                  Together we can make an impactful difference directly benefiting our Veteran Community
                </Typography>
                <Box sx={{ mb: 4 }}>
                  {[
                    "Providing essential services to veterans transitioning to civilian life",
                    "Support Single Parent Veteran Families with healthy family life assistance",
                    "Fund critical Veteran support programs through our partnered organizations",
                    "Create opportunities for Veterans to maintain independence and dignity life standards"
                  ].map((point, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mb: 2,
                        '&:last-child': { mb: 0 }
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.primary.main,
                          mr: 2,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                        }}
                      >
                        {point}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Button
                  component={RouterLink}
                  to="/donate"
                  variant="contained"
                  color="primary"
                  size="large"
                >
                  Donate Your Vehicle
                </Button>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h2"
              align="center"
              sx={{
                mb: 3,
                fontWeight: 700,
              }}
            >
              Ready to Make a Difference?
            </Typography>
            <Typography
              variant="h5"
              align="center"
              sx={{
                mb: 4,
                maxWidth: '800px',
                mx: 'auto',
                opacity: 0.9,
              }}
            >
              Your vehicle donation can help change a veteran&apos;s life. Join us in supporting our heroes.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <Button
                component={RouterLink}
                to="/donate"
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                }}
              >
                Donate Now
              </Button>
              <Button
                component={RouterLink}
                to="/contact"
                variant="outlined"
                sx={{
                  px: 4,
                  py: 1.5,
                  color: 'white',
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
                size="large"
              >
                Contact Us
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
